// The mock's file storage.
//
// A real upload leaves the bytes on a server and hands back a URL that keeps
// working. The mock had nowhere to put them, so `photo.url` came back undefined
// and every screen after step 2 fell back to a grey box — the object URL the
// dropzone was showing dies with the page that created it.
//
// So the bytes live here, as data URLs in sessionStorage, keyed by the same
// asset id the mock's uploadMedia invented. Same lifetime as the mock session
// they belong to (one tab, cleared together), which is the point: reloading
// step 4 or arriving there from Stripe finds the photo exactly where a real
// backend would have left it.
//
// Only what casting sees is kept — the CROPPED export, ~150KB of JPEG. The
// original a phone produced is several megabytes, and base64 adds a third
// again; two of those would exceed the ~5MB sessionStorage budget on their own.
// That is why "Edit photo frame" is offered only while the original is still in
// memory (SubmitMaterialsPage guards it on `original`).

import { IMediaAssetDto, MediaKind } from "./types";

const KEY = "sfMockAssets.v1";

interface IStoredAsset {
  assetId: number;
  kind: MediaKind;
  fileName: string;
  sizeBytes: number;
  uploadedAtUtc: string;
  // The bytes, as `data:image/jpeg;base64,…`. Undefined for a resume: nothing
  // in the flow renders a PDF, so storing one would spend the budget on
  // something no screen reads.
  dataUrl?: string;
}

type AssetMap = { [assetId: string]: IStoredAsset };

const readAll = (): AssetMap => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as AssetMap) : {};
  } catch (e) {
    return {};
  }
};

const writeAll = (assets: AssetMap) => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(assets));
  } catch (e) {
    // Over quota, or storage disabled. The upload still "succeeded" as far as
    // the flow is concerned — the photo just will not survive a reload, which
    // is the behaviour we had before this file existed.
  }
};

const readAsDataUrl = (file: Blob): Promise<string | undefined> =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(file);
    } catch (e) {
      resolve(undefined);
    }
  });

/**
 * Records an uploaded asset, keeping the bytes when they are worth keeping.
 * Returns the DTO the mock hands back, so `url` and the real filename come from
 * one place.
 */
export const putMockAsset = async (
  asset: IMediaAssetDto,
  file?: Blob
): Promise<IMediaAssetDto> => {
  const dataUrl =
    file && asset.kind === "photo" ? await readAsDataUrl(file) : undefined;
  const stored: IStoredAsset = {
    assetId: asset.assetId,
    kind: asset.kind,
    fileName: asset.fileName,
    sizeBytes: asset.sizeBytes,
    uploadedAtUtc: asset.uploadedAtUtc,
    dataUrl,
  };

  const assets = readAll();
  // One asset per kind. Replacing a photo three times should not leave three
  // copies of it sitting in a 5MB budget.
  Object.keys(assets).forEach((id) => {
    if (assets[id] && assets[id].kind === asset.kind) delete assets[id];
  });
  assets[String(asset.assetId)] = stored;
  writeAll(assets);

  return { ...asset, url: dataUrl };
};

/**
 * The asset for an id, as a DTO. Returns undefined for an id this tab never
 * uploaded — a session from before this store existed, or another tab's.
 */
export const getMockAsset = (assetId?: number | null): IMediaAssetDto | undefined => {
  if (!assetId) return undefined;
  const stored = readAll()[String(assetId)];
  if (!stored) return undefined;
  return {
    assetId: stored.assetId,
    kind: stored.kind,
    fileName: stored.fileName,
    sizeBytes: stored.sizeBytes,
    uploadedAtUtc: stored.uploadedAtUtc,
    url: stored.dataUrl,
  };
};

/**
 * What this tab uploaded, of one kind: what PM would list for the account after a reload. The
 * mock keeps one per kind; that one is the main (PM's default photo, the newest resume).
 */
export const listMockAssets = (kind: MediaKind): IMediaAssetDto[] =>
  Object.keys(readAll())
    .map((id) => getMockAsset(Number(id)))
    .filter((a): a is IMediaAssetDto => !!a && a.kind === kind)
    .map((a) => ({ ...a, isMain: true, resumeType: kind === "resume" ? "pdf" : undefined }));

export const clearMockAssets = () => {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // ignore
  }
};
